import React, { useState, useEffect } from "react";
import { X, Bell, Loader } from "lucide-react";
import type { WatchRules } from "../services/TrackingService";
import { getProductImageUrl } from "../utils/imageUtils";
import {
  formatPrice,
  parseTargetPrice,
} from "./ProductSearchTrackingModal.utils";

interface AddTrackerRequest {
  upc: string;
  zipCode: string;
  targetPrice: number;
  productName?: string;
  productImage?: string;
  productCategory?: string;
  msrp?: number;
  currentPrice?: number;
  rules?: WatchRules;
}

interface ProductSearchTrackingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AddTrackerRequest) => void;
  isLoading: boolean;
  productUpc?: string;
  searchZipCode?: string;
  productName?: string;
  productImage?: string;
  productCategory?: string;
  msrp?: number;
  currentPrice?: number;
  trackerCount?: number;
  trackerLimit?: number;
}

export default function ProductSearchTrackingModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  productUpc = "",
  searchZipCode = "",
  productName = "",
  productImage = "",
  productCategory = "",
  msrp = 0,
  currentPrice = 0,
  trackerCount,
  trackerLimit,
}: ProductSearchTrackingModalProps) {
  const [targetPrice, setTargetPrice] = useState("");
  const [notifyBackInStock, setNotifyBackInStock] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setTargetPrice(currentPrice > 0 ? currentPrice.toFixed(2) : "");
    }
  }, [isOpen, currentPrice]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const price = parseTargetPrice(targetPrice);
    if (price === null) return;
    onSubmit({
      upc: productUpc,
      zipCode: searchZipCode,
      targetPrice: price,
      productName,
      productImage,
      productCategory,
      msrp,
      currentPrice,
      rules: {
        mode: "price",
        minDiscount: 0,
        minProfit: 0,
        minRoi: 0,
        resalePrice: 0,
        marketplaceFeeRate: 0,
        notifyBackInStock,
      },
    });
  };

  const handleClose = () => {
    setTargetPrice("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[9999] p-4 bg-black/50 dark:bg-black/70"
      onClick={handleClose}
    >
      <div
        className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl max-w-sm w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-5 py-4 border-b border-gray-100 dark:border-dark-700">
          <button
            onClick={handleClose}
            className="absolute right-3 top-3 p-1.5 text-gray-400 dark:text-dark-400 hover:text-gray-600 dark:hover:text-dark-200 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 text-gray-900 dark:text-white">
            <Bell className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <h2 className="text-lg font-semibold">Create alert</h2>
          </div>
        </div>

        {/* Alert Usage Banner */}
        {trackerCount !== undefined && trackerLimit !== undefined && (
          <div className={`px-5 py-3 ${trackerCount >= trackerLimit - 3 ? "bg-amber-50 dark:bg-amber-900/30 border-b border-amber-100 dark:border-amber-800" : "bg-purple-50 dark:bg-purple-900/30 border-b border-purple-100 dark:border-purple-800"}`}>
            <div className="flex items-center justify-between mb-1.5">
              <span className={`text-xs font-semibold ${trackerCount >= trackerLimit - 3 ? "text-amber-700 dark:text-amber-300" : "text-purple-700 dark:text-purple-300"}`}>
                {trackerLimit - trackerCount} of {trackerLimit} alerts remaining
              </span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                trackerCount >= trackerLimit - 3 
                  ? "bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200" 
                  : "bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200"
              }`}>
                {trackerCount} used
              </span>
            </div>
            <div className="h-2 bg-white dark:bg-dark-700 rounded-full overflow-hidden shadow-inner">
              <div
                className={`h-full rounded-full transition-all ${
                  trackerCount >= trackerLimit - 3 ? "bg-amber-500" : "bg-purple-500"
                }`}
                style={{ width: `${Math.min((trackerCount / trackerLimit) * 100, 100)}%` }}
              />
            </div>
            {trackerCount >= trackerLimit - 3 && (
              <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1.5">
                ⚠️ Running low on alerts. You can manage existing alerts in the Alerts tab.
              </p>
            )}
          </div>
        )}

        {/* Product Info */}
        <div className="px-5 py-4 border-b border-gray-100 dark:border-dark-700">
          <div className="flex gap-3">
            {productImage ? (
              <img
                src={getProductImageUrl(productImage, "100")}
                alt={productName}
                className="w-16 h-16 rounded-lg object-cover bg-gray-100 dark:bg-dark-700 flex-shrink-0"
              />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-gray-100 dark:bg-dark-700 flex items-center justify-center flex-shrink-0">
                <Bell className="w-6 h-6 text-gray-300 dark:text-dark-500" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white line-clamp-2 leading-snug">
                {productName || "Product"}
              </p>
              {currentPrice > 0 && (
                <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                  {formatPrice(currentPrice)}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5">
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-2">
            Alert me at or below
          </label>
          <div className="relative mb-5">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-dark-400 font-medium">$</span>
            <input
              type="number"
              value={targetPrice}
              onChange={(e) => setTargetPrice(e.target.value)}
              className="w-full pl-9 pr-4 py-3 text-lg font-semibold border-2 border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 text-gray-900 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all placeholder:text-gray-400 dark:placeholder:text-dark-500"
              placeholder="0.00"
              min="0"
              step="0.01"
              autoFocus
              required
            />
          </div>

          <label className="mb-5 flex items-center justify-between rounded-xl border border-gray-200 dark:border-dark-600 px-3 py-2.5">
            <span className="text-xs font-semibold text-gray-700 dark:text-dark-200">Also notify when back in stock</span>
            <input type="checkbox" checked={notifyBackInStock} onChange={(e) => setNotifyBackInStock(e.target.checked)} className="h-4 w-4 accent-primary-600" />
          </label>

          <button
            type="submit"
            disabled={isLoading || !targetPrice.trim() || !productUpc}
            className="w-full py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg shadow-primary-600/20"
          >
            {isLoading ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>Adding Alert...</span>
              </>
            ) : (
              <>
                <Bell className="w-5 h-5" />
                <span>Create alert</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
